import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  SMGetWorkCompleted_Result,
  WorkCompletedCustomFieldDefs,
  SMLookupResult,
  SMGetWorkOrderScopesByForm_Result,
  SMGetFormsByScope_Result,
  SMGetWorkOrderTrips_Result,
  SMInsertUpdateSMWorkCompleted_Result,
  SMGetServiceItemForms_Result,
  GetSMServiceSiteAttachments_Result,
  SMGetWorkOrderScopes_Result,
  SMGetTripsAndTechniciansByWorkOrder_Result,
} from '@typeDefs/field-service/responses/field-service';
import { VistaCustomField } from '@typeDefs/general/general';
import { ApiDataResponse } from '@shared/model/api-response.model';
import { SMInsertUpdateSMWorkCompleted_Request } from '@typeDefs/field-service/requests/field-service';
import { Observable } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class SmService {
  http: HttpClient;
  constructor(http: HttpClient) {
    this.http = http;
  }

  defaultAssignmentsGet(checklistTemplateID: number) {
    const url = '/SM/DefaultAssignmentsGet?checklistTemplateID=' + checklistTemplateID;
    return this.http.get(url).toPromise();
  }

  defaultAssignmentGetChoices(assignType: string) {
    const url = '/SM/DefaultAssignmentGetChoices?assignType=' + assignType;
    return this.http.get(url).toPromise();
  }

  defaultAssignmentAddEdit(
    checklistDefaultAssignID: number,
    checklistTemplateID: number,
    assignType: string,
    assignValue: string,
    completeRequired?: boolean
  ) {
    return this.http
      .post('/SM/DefaultAssignmentAddEdit', {
        checklistDefaultAssignID: checklistDefaultAssignID,
        checklistTemplateID: checklistTemplateID,
        assignType: assignType,
        assignValue: assignValue,
        completeRequired: completeRequired,
      })
      .toPromise();
  }

  defaultAssignmentRemove(checklistDefaultAssignID: number, checklistTemplateID: number) {
    return this.http
      .post('/SM/DefaultAssignmentRemove', {
        checklistTemplateID: checklistTemplateID,
        checklistDefaultAssignID: checklistDefaultAssignID,
      })
      .toPromise();
  }

  getTechnicians(sMCo?: number) {
    const url = '/SM/GetTechnicians' + (sMCo != null ? '?sMCo=' + sMCo : '');
    return this.http.get(url).toPromise();
  }

  getTripsAndTechniciansByWorkOrder(
    smCo: number,
    workOrder: number,
    tripID: bigint
  ): Observable<SMGetTripsAndTechniciansByWorkOrder_Result[]> {
    const url = '/SM/GetTripsAndTechniciansByWorkOrder';
    const params = new HttpParams().set('smCo', smCo).set('workOrder', workOrder).set('tripID', tripID.toString());
    return this.http.get<SMGetTripsAndTechniciansByWorkOrder_Result[]>(url, { params });
  }

  getWorkOrderTrips(extendURL: string) {
    const url = '/SM/GetWorkOrderTrips' + extendURL;
    return this.http.get<SMGetWorkOrderTrips_Result[]>(url).toPromise();
  }

  getClockInStatus() {
    const url = '/SM/GetClockInStatus';
    return this.http.get(url).toPromise();
  }

  getSignerContacts(sMWorkOrderID: any, sMTripID: any) {
    const url = '/SM/GetSignerContacts?sMWorkOrderID=' + sMWorkOrderID + '&sMTripID=' + sMTripID;
    return this.http.get(url).toPromise();
  }

  getCommonLookups(sMCo: string) {
    const url = '/SM/GetCommonLookups';
    const params = new HttpParams().set('sMCo', sMCo);
    return this.http.get<SMLookupResult[]>(url, { params: params }).toPromise();
  }

  getCommonWoLookups(smWorkOrderID: string) {
    const url = '/SM/GetCommonWoLookups';
    const params = new HttpParams().set('smWorkOrderID', smWorkOrderID);
    return this.http.get<SMLookupResult[]>(url, { params: params }).toPromise();
  }
  getSMPayTypes(sMCo: number) {
    const url = '/SM/GetSMPayTypes';
    const params = new HttpParams().set('sMCo', sMCo);
    return this.http.get(url, { params: params }).toPromise();
  }

  getSettingsBySMCo(sMCo: number) {
    const url = '/SM/GetSettingsBySMCo?sMCo=' + sMCo;
    return this.http.get(url).toPromise();
  }

  syncAll(fromDate: any, toDate: any, isUnscheduled: any) {
    const url = '/SM/SyncAll?fromTripDateStr=' + fromDate + '&toTripDateStr=' + toDate + '&includeUnscheduled=' + isUnscheduled;
    return this.http.get(url).toPromise();
  }

  getVPAttachmentList(uniqueAttchID: any, includeData: any) {
    const url = '/Document/GetVPAttachmentList?uniqueAttchID=' + uniqueAttchID + '&includeData=' + includeData;
    return this.http.get(url).toPromise();
  }

  getWorkCompleted(sMWorkOrderScopeID: number) {
    const url = '/SM/GetWorkCompleted';
    const params = new HttpParams().set('sMWorkOrderScopeID', sMWorkOrderScopeID);
    return this.http.get<SMGetWorkCompleted_Result[]>(url, { params: params }).toPromise();
  }

  getFormTemplateOptions(SMWorkOrderScopeID: any) {
    const url = '/SM/GetFormTemplateOptions?sMWorkOrderScopeID=' + SMWorkOrderScopeID;
    return this.http.get(url).toPromise();
  }

  getFormsPerScope(extendURL: string) {
    const url = '/SM/GetFormsPerScope' + extendURL;
    return this.http.get(url).toPromise();
  }

  getWorkOrderScopes(extendURL: string): Promise<SMGetWorkOrderScopes_Result[]> {
    const url = '/SM/GetWorkOrderScopes' + extendURL;
    return this.http.get<SMGetWorkOrderScopes_Result[]>(url).toPromise();
  }

  getScopePurchaseOrders(extendURL: string) {
    const url = '/SM/GetScopePurchaseOrders' + extendURL;
    return this.http.get(url).toPromise();
  }

  getScopeTasks(extendURL: string) {
    const url = '/SM/GetScopeTasks' + extendURL;
    return this.http.get(url).toPromise();
  }

  workOrderScopeInsertUpdate(data: any) {
    return this.http
      .post('/SM/WorkOrderScopeInsertUpdate', {
        sMWorkOrderID: data.sMWorkOrderID,
        sMWorkOrderScopeID: data.sMWorkOrderScopeID,
        sMWorkScope: data.sMWorkScope,
        priority: data.priority,
        scopeDetails: data.scopeDetails,
        sMCallType: data.sMCallType,
        serviceItem: data.serviceItem,
        sMScopeNotes: data.sMScopeNotes,
        phase: data.phase,
      })
      .toPromise();
  }

  getEQSIMA(extendURL: any) {
    const url = extendURL;
    return this.http.get(url).toPromise();
  }

  getRevCode(extendURL: string) {
    const url = '/SM/GetEquipRevCode' + extendURL;
    return this.http.get(url).toPromise();
  }

  insertUpdateSMWorkCompleted(data: SMInsertUpdateSMWorkCompleted_Request) {
    return this.http
      .post<ApiDataResponse<SMInsertUpdateSMWorkCompleted_Result>>('/SM/InsertUpdateSMWorkCompleted', { smComParam: data })
      .toPromise();
  }

  deleteSMWorkCompleted(sMWorkCompletedID: any) {
    return this.http
      .post('/SM/DeleteSMWorkCompleted', {
        sMWorkCompletedID: sMWorkCompletedID,
      })
      .toPromise();
  }

  updateTripNotes(sMTripID: any, tripNotes: any, workOrderDescription: any, tripDescription: any) {
    return this.http
      .post('/SM/UpdateTripNotes', {
        sMTripID: sMTripID,
        tripNotes: tripNotes,
        workOrderDescription: workOrderDescription,
        tripDescription: tripDescription,
      })
      .toPromise();
  }

  updateTripStatus(sMTripID: any, tripStatus: any) {
    return this.http
      .post('/SM/UpdateTripStatus', {
        sMTripID: sMTripID,
        tripStatus: tripStatus,
      })
      .toPromise();
  }

  createNewForm(checklistTemplateID: any, sMWorkOrderScopeID: any, sMTripID: any) {
    return this.http
      .post('/SM/CreateNewForm', {
        checklistTemplateID: checklistTemplateID,
        sMWorkOrderScopeID: sMWorkOrderScopeID,
        sMTripID: sMTripID,
      })
      .toPromise();
  }

  deleteForm(scopeChecklistInstanceID: any) {
    return this.http
      .post('/SM/DeleteForm', {
        scopeChecklistInstanceID: scopeChecklistInstanceID,
      })
      .toPromise();
  }

  updateScopeTask(sMWorkOrderScopeID: any, sMScopeTaskID: any, completed: any) {
    return this.http
      .post('/SM/UpdateScopeTask', {
        sMWorkOrderScopeID: sMWorkOrderScopeID,
        sMScopeTaskID: sMScopeTaskID,
        completed: completed,
      })
      .toPromise();
  }

  updateSettings(enableOfflineFS: any) {
    return this.http
      .post('/SM/UpdateSettings', {
        enableOfflineFS: enableOfflineFS,
      })
      .toPromise();
  }

  pPOInsert(
    sMWorkOrderScopeID: any,
    pODescription: any,
    userPONumber: any,
    pORoughAmount: any,
    aPVMKeyID: any,
    requestDateStr: any,
    sMCostType: any,
    sMTripID: any,
    customFields: VistaCustomField[]
  ) {
    const url = '/SM/POInsert';
    return this.http
      .post(url, {
        sMWorkOrderScopeID: sMWorkOrderScopeID,
        pODescription: pODescription,
        userPONumber: userPONumber,
        pORoughAmount: pORoughAmount,
        aPVMKeyID: aPVMKeyID,
        requestDateStr: requestDateStr,
        sMCostType: sMCostType,
        sMTripID: sMTripID,
        customFields: customFields,
      })
      .toPromise();
  }

  getFormsPerScopeMulti(sMWorkOrderScopeID: any, sMTripID: any): any {
    return this.http.post('/SM/GetFormsPerScopeMulti', { sMWorkOrderScopeID: sMWorkOrderScopeID, sMTripID: sMTripID }).toPromise();
  }

  completeTrip(data: any) {
    return this.http
      .post('/SM/CompleteTrip', {
        sMTripID: data.sMTripID,
        sMWorkOrderID: data.sMWorkOrderID,
        revisitRequired: data.revisitRequired,
        revisitReason: data.revisitReason,
        revisitNotes: data.revisitNotes,
        signatureImage: data.signatureImage,
        signatureLat: data.signatureLat,
        signatureLong: data.signatureLong,
        hQContactID: data.hQContactID,
        firstName: data.firstName,
        lastName: data.lastName,
        title: data.title,
        email: data.email,
        scopes: data.scopes,
        unique: data.unique,
        fileName: data.fileName,
        emailList: data.emailList,
        subject: data.subject,
        justComplete: data.justComplete,
        sMCO: data.sMCO,
        phone: data.phone,
        ccEmailList: data.ccEmailList,
        justSendEmail: data.justSendEmail,
        trip: data.trip,
        techName: data.techName,
        companyName: data.companyName,
      })
      .toPromise();
  }

  getServiceItemDetails(sMServiceSiteID: any, sMServiceItemID: any) {
    return this.http
      .get(
        '/SM/GetServiceItemDetails?' +
          (sMServiceSiteID != null ? 'sMServiceSiteID=' + sMServiceSiteID : 'sMServiceItemID=' + sMServiceItemID)
      )
      .toPromise();
  }

  serviceItemWOHistory(sMServiceSiteID: any, sMTripID: any, sMServiceItemID: any) {
    return this.http
      .get(
        '/SM/ServiceItemWOHistory?sMTripID=' +
          sMTripID +
          (sMServiceSiteID != null ? '&sMServiceSiteID=' + sMServiceSiteID : '') +
          (sMServiceItemID != null ? '&sMServiceItemID=' + sMServiceItemID : '')
      )
      .toPromise();
  }

  updateSiteNotes(sMServiceSiteID: any, siteNotes: any) {
    return this.http.post('/SM/UpdateSiteNotes', { sMServiceSiteID: sMServiceSiteID, siteNotes: siteNotes }).toPromise();
  }

  getCustomers(SMCo: any) {
    return this.http.get('/SM/GetCustomers?sMCo=' + SMCo + '&includeInactive=false').toPromise();
  }

  getSitesByCustomer(queryStrArr: any) {
    return this.http.get('/SM/GetSitesByCustomer?' + queryStrArr).toPromise();
  }

  getServiceCenters(SMCo: any) {
    return this.http.get('/SM/GetServiceCenters?sMCo=' + SMCo + '&includeInactive=false').toPromise();
  }

  workOrderInsertUpdate(data) {
    return this.http
      .post('/SM/WorkOrderInsertUpdate', {
        sMCo: data.sMCo,
        sMWorkOrderID: data.sMWorkOrderID,
        sMCustomerID: data.sMCustomerID,
        sMServiceSiteID: data.sMServiceSiteID,
        workOrderDescription: data.workOrderDescription,
        requestedBy: data.requestedBy,
        requestedByPhone: data.requestedByPhone,
        contactName: data.contactName,
        contactPhone: data.contactPhone,
        leadTechnician: data.leadTechnician,
        serviceCenter: data.serviceCenter,
        sMWorkOrderNotes: data.sMWorkOrderNotes,
        includeUnscheduled: data.includeUnscheduled,
        sMTripID: data.sMTripID,
        workOrder: data.workOrder,
        sMCallType: data.sMCallType,
        division: data.division,
      })
      .toPromise();
  }

  serviceItemInsertUpdate(
    sMServiceItemID: any,
    sMCo: any,
    serviceSite: any,
    serviceItem: any,
    itemDescription: any,
    manufacturer: any,
    model: any,
    yearManufactured: any,
    location: any,
    sMTypeID: any,
    serialNumber: any,
    active: any,
    serviceItemSummary: any,
    QRCodeID: any
  ) {
    return this.http
      .post('/SM/ServiceItemInsertUpdate', {
        sMServiceItemID: sMServiceItemID,
        sMCo: sMCo,
        serviceSite: serviceSite,
        serviceItem: serviceItem,
        itemDescription: itemDescription,
        manufacturer: manufacturer,
        model: model,
        yearManufactured: yearManufactured,
        location: location,
        sMTypeID: sMTypeID,
        serialNumber: serialNumber,
        active: active,
        serviceItemSummary: serviceItemSummary,
        QRCodeID: QRCodeID,
      })
      .toPromise();
  }

  getServiceItemParts(sMServiceItemID: any, sMServiceSiteID: any) {
    const url = '/SM/GetServiceItemParts?sMServiceItemID=' + sMServiceItemID + '&sMServiceSiteID=' + sMServiceSiteID;
    return this.http.get(url).toPromise();
  }

  saveTripSignatureList(
    sMTripID: any,
    sMWorkOrderID: any,
    sMScopesString: any,
    signatureImage: any,
    signatureLat: any,
    signatureLong: any,
    hQContactID: any,
    firstName: any,
    lastName: any,
    title: any,
    email: any,
    phone: any
  ) {
    return this.http
      .post('/SM/SaveTripSignatureList', {
        sMTripID: sMTripID,
        sMWorkOrderID: sMWorkOrderID,
        sMScopesString: sMScopesString,
        signatureImage: signatureImage,
        signatureLat: signatureLat,
        signatureLong: signatureLong,
        hQContactID: hQContactID,
        firstName: firstName,
        lastName: lastName,
        title: title,
        email: email,
        phone: phone,
      })
      .toPromise();
  }

  invoiceProcess(sMWorkOrderID: any, sMScopeExcludeList: any, paymentRef: any) {
    return this.http
      .post('/SM/InvoiceProcess', {
        sMWorkOrderID: sMWorkOrderID,
        sMScopeExcludeList: sMScopeExcludeList,
        paymentRef: paymentRef,
      })
      .toPromise();
  }

  tripInsertUpdate(
    sMWorkOrderID: any,
    sMTripID: any,
    tripDescription: any,
    technician: any,
    scheduledDateStr: any,
    scheduleTimeStr: any,
    isWODashboard: any,
    tripNotes: any
  ) {
    return this.http
      .post('/SM/TripInsertUpdate', {
        sMWorkOrderID: sMWorkOrderID,
        sMTripID: sMTripID,
        tripDescription: tripDescription,
        technician: technician,
        scheduledDateStr: scheduledDateStr,
        scheduleTimeStr: scheduleTimeStr,
        isWODashboard: isWODashboard,
        tripNotes: tripNotes,
      })
      .toPromise();
  }

  getSignatures(sMTripID: any, sMScopeString: any) {
    const url = '/SM/GetSignatures?sMTripID=' + sMTripID + '&sMScopeString=' + sMScopeString;
    return this.http.get(url).toPromise();
  }

  getVPAttachmentData(attachmentID: any) {
    const url = '/DailyReports/GetVPAttachmentData?attachmentID=' + attachmentID + '&turnOnMaxWidth=false';
    return this.http.get(url).toPromise();
  }

  getVCMttachmentData(contentTypeName: any, docID: any) {
    const url = '/DailyReports/GetVCMttachmentData?contentTypeName=' + contentTypeName + '&docID=' + docID;
    return this.http.get(url).toPromise();
  }

  updateVPAttachment(attachmentID: any, fileDescription: any, attachmentTypeID: any, sMWorkOrderScopeID: any) {
    return this.http
      .post('/SM/UpdateVPAttachment', {
        attachmentID: attachmentID,
        fileDescription: fileDescription,
        attachmentTypeID: attachmentTypeID,
        sMWorkOrderScopeID: sMWorkOrderScopeID,
      })
      .toPromise();
  }

  getServiceItemIDByQRCode(qrCodeID: string): Promise<ApiDataResponse<number>> {
    const url = '/SM/GetServiceItemIDByQRCode';
    const params = new HttpParams().set('qrCodeID', qrCodeID);
    return <Promise<ApiDataResponse<number>>>this.http.get(url, { params }).toPromise();
  }

  offlinePost(url: any, data: any) {
    return this.http.post(url, data).toPromise();
  }

  getCompanies() {
    const url = '/SM/GetCompanies';
    return this.http.get(url).toPromise();
  }

  getServiceWorkOrders(queryStrArr: any) {
    const url = '/SM/GetServiceWorkOrders?' + queryStrArr.join('&');
    return this.http.get(url).toPromise();
  }

  getTripsByWorkOrder(sMWorkOrderID?: number) {
    const url = '/SM/GetTripsByWorkOrder?sMWorkOrderID=' + sMWorkOrderID;
    return this.http.get(url).toPromise();
  }

  getTripSignatures(sMTripID?: number) {
    const url = '/SM/GetTripSignatures?sMTripID=' + sMTripID;
    return this.http.get(url).toPromise();
  }

  getWCCustomFieldDefinitions() {
    const url = '/SM/GetWCCustomFieldDefinitions';
    return this.http.get<WorkCompletedCustomFieldDefs>(url).toPromise();
  }

  getWorkOrderTripsByEmployee(employee: any, prCo: any) {
    const url = '/SM/GetWorkOrderTripsByEmployee';
    const params = new HttpParams().set('prCo', prCo).set('employee', employee);
    return this.http.get(url, { params: params }).toPromise();
  }

  getWorkOrderScopesByForm(
    checklistTemplateID: number,
    sMCo: number,
    workOrder: number,
    checklistInstanceID: number
  ): Promise<SMGetWorkOrderScopesByForm_Result[]> {
    const url = '/SM/GetWorkOrderScopesByForm';
    let params = new HttpParams().set('checklistTemplateID', checklistTemplateID);
    params = params.set('sMCo', sMCo);
    params = params.set('workOrder', workOrder);
    params = params.set('checklistInstanceID', checklistInstanceID);
    return this.http.get<SMGetWorkOrderScopesByForm_Result[]>(url, { params: params }).toPromise();
  }

  getFormsByScope(
    checklistTemplateID: number,
    sMWorkOrderScopeID: number,
    checklistInstanceID: number
  ): Promise<SMGetFormsByScope_Result[]> {
    const url = '/SM/GetFormsByScope';
    let params = new HttpParams().set('checklistTemplateID', checklistTemplateID);
    params = params.set('sMWorkOrderScopeID', sMWorkOrderScopeID);
    params = params.set('checklistInstanceID', checklistInstanceID);
    return this.http.get<SMGetFormsByScope_Result[]>(url, { params: params }).toPromise();
  }

  getSMServiceSiteAttachments(smServiceSiteId: number, attachmentTypeIds: string): Observable<GetSMServiceSiteAttachments_Result[]> {
    const url = '/SM/GetSMServiceSiteAttachments';
    const params = new HttpParams().set('smServiceSiteId', smServiceSiteId).set('attachmentTypeIds', attachmentTypeIds);
    return this.http.get<GetSMServiceSiteAttachments_Result[]>(url, { params: params });
  }
}
